import { eachDayOfInterval, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns"
import { useEffect, useMemo } from "react"

/**
 * Hook to manage window resize events with debounce
 * @param {Function} callback - Function to call when resize is complete
 */
export const useWindowResize = callback => {
  useEffect(() => {
    let resizeTimer

    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        callback()
      }, 150) // Debounce resize events
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(resizeTimer)
    }
  }, [callback])
}

/**
 * Hook to get calendar days for the month
 * @param {Date} month - Month to show in calendar
 * @returns {Object} Calendar data object with month and calendarDays
 */
export const useGetMonthData = (month = new Date()) => {
  const calendarDays = useMemo(() => {
    const startDate = startOfWeek(startOfMonth(month))
    const endDate = endOfWeek(endOfMonth(month))
    return eachDayOfInterval({ start: startDate, end: endDate })
  }, [month])

  return { month, calendarDays }
}

/**
 * Hook to organize events by day
 * @param {Array} events - List of events to organize
 * @returns {Object} Event manager with getEventsForDay method
 */
export const useEventManager = (events = []) => {
  return useMemo(() => {
    const eventsMap = {}

    events.forEach(event => {
      const dateKey = event.start.split("T")[0]
      if (!eventsMap[dateKey]) eventsMap[dateKey] = []
      eventsMap[dateKey].push(event)
    })

    return {
      getEventsForDay: day => eventsMap[day] || [],
    }
  }, [events])
}
